/* eslint-disable @typescript-eslint/unbound-method */
import { Children, ReactElement } from 'react';
import { eventChannel } from 'redux-saga';
import {
  takeLatest, select, call, put, take, fork, all, race, delay, spawn,
} from 'redux-saga/effects';

import { WorkflowState } from 'ia-react-core/~/interfaces/WorkflowState';
import { ExitGuardOptions } from 'ia-react-core/~/interfaces/ExitGuard';
import { ConfirmGuardAction } from 'ia-react-core/~/interfaces/ConfirmGuardAction';
import { BlockGuardAction } from 'ia-react-core/~/interfaces/BlockGuardAction';
import { RedirectGuardAction } from 'ia-react-core/~/interfaces/RedirectGuardAction';
import { EntryGuardOptions } from 'ia-react-core/~/interfaces/EntryGuard';
import MoveWorkflowBackwardById from 'ia-react-core/~/actions/workflow/MoveWorkflowBackwardById';
import MoveWorkflowForwardByName from 'ia-react-core/~/actions/workflow/MoveWorkflowForwardByName';
import MoveWorkflowForwardById from 'ia-react-core/~/actions/workflow/MoveWorkflowForwardById';
import MoveWorkflowBackwardByName from 'ia-react-core/~/actions/workflow/MoveWorkflowBackwardByName';
import MoveWorkflowToStepById from 'ia-react-core/~/actions/workflow/MoveWorkflowToStepById';
import MoveWorkflowToStepByName from 'ia-react-core/~/actions/workflow/MoveWorkflowToStepByName';
import getLanguageCode from 'ia-react-core/~/utils/language/getLanguageCode';
import { LanguageState } from 'ia-react-core/~/interfaces/LanguageState';
import Router from 'ia-react-core/~/services/router/Router';
import { BackwardGuardAction } from 'ia-react-core/~/interfaces/BackwardGuardAction';
import { ForwardGuardAction } from 'ia-react-core/~/interfaces/ForwardGuardAction';
import { HistoryLocation } from 'ia-react-core/~/interfaces/HistoryLocation';
import getScoped from '../../../utils/redux/getScoped';

import openModalByName from '../../../actions/modal-fn/openModalByName';
import closeModalByName from '../../../actions/modal-fn/closeModalByName';
import confirmWorkflowStepEntryById from '../../../actions/workflow-fn/confirmWorkflowStepEntryById';
import cancelWorkflowStepEntryById from '../../../actions/workflow-fn/cancelWorkflowStepEntryById';
import moveWorkflowBackwardById from '../../../actions/workflow-fn/moveWorkflowBackwardById';
import moveWorkflowBackwardByName from '../../../actions/workflow-fn/moveWorkflowBackwardByName';
import moveWorkflowForwardById from '../../../actions/workflow-fn/moveWorkflowForwardById';
import moveWorkflowForwardByName from '../../../actions/workflow-fn/moveWorkflowForwardByName';
import moveWorkflowToStepById from '../../../actions/workflow-fn/moveWorkflowToStepById';
import moveWorkflowToStepByName from '../../../actions/workflow-fn/moveWorkflowToStepByName';
import commitWorkflowStepById from '../../../actions/workflow-fn/commitWorkflowStepById';
import workflowMovedToStepById from '../../../actions/workflow-fn/workflowMovedToStepById';
import { WorkflowComposedProps } from './Workflow';
import { StepProps } from '../Step';

const makeStateSelector = (path: string, scope: string[]) => (state: WorkflowState) => getScoped(state, path, scope) as string;

const GUARD_ACTION_TYPES = {
  BLOCK: 'BLOCK',
  REDIRECT: 'REDIRECT',
  FORWARD: 'FORWARD',
  BACKWARD: 'BACKWARD',
  CONFIRM: 'CONFIRM',
} as const;

function* getCurrentStepName({ scope, name }: WorkflowComposedProps) {
  const currentStepSelector: ReturnType<typeof makeStateSelector> = yield call(makeStateSelector, `${name}.currentStepName`, scope);
  const currentStepName: string = yield select(currentStepSelector);
  return currentStepName;
}

function normalizePath(pathname: string, basename: string) {
  const localPathname = pathname.replace(new RegExp(`^${basename}`), '');
  return localPathname.startsWith('/') ? localPathname : `/${localPathname}`;
}

function findStepByPathname(steps: ReactElement<StepProps>[], pathname: string, basename: string) {
  return steps.find((step) => {
    const flatPath: string | string[] = (!step.props.path || typeof step.props.path === 'string' || Array.isArray(step.props.path)) ? step.props.path as string | string[] : [step.props.path.fr, step.props.path.en].flat();
    const resolvedPaths = Array.isArray(flatPath) ? flatPath : [flatPath];
    const matchesPath = resolvedPaths.some((path) => (
      Router.matchPath(normalizePath(pathname, basename), {
        path: step.props.path ? normalizePath(path, basename) : null,
        exact: typeof step.props.exact !== 'undefined' ? step.props.exact : true,
      })
    ));

    return matchesPath;
  });
}

function findStepByName(steps: ReactElement<StepProps>[], stepName: string) {
  return steps.find((step) => step.props.name === stepName);
}

function findStepIndexByName(steps: ReactElement<StepProps>[], stepName: string) {
  return steps.findIndex((step) => step.props.name === stepName);
}

function* getSteps({ children }: WorkflowComposedProps) {
  const childrenArray: ReactElement<StepProps>[] = yield call(Children.toArray, children);
  return childrenArray;
}

function* getStepByPathname(props: WorkflowComposedProps, pathname: string, basename: string) {
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const step: ReactElement<StepProps> = yield call(findStepByPathname, steps, pathname, basename);
  return step;
}

function* getActiveStepFromLocation(props: WorkflowComposedProps) {
  const { history, basename } = props;
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const step: ReactElement<StepProps> = yield call(findStepByPathname, steps, history.location.pathname, basename);
  return step;
}

function* moveStep(argsGetter: () => WorkflowComposedProps, offset: number, values: Object, { replace, state }: { replace?: boolean; state?: any }) {
  const props = argsGetter();
  const { workflowId } = props;
  const currentStepName: string = yield call(getCurrentStepName, props);
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const currentStepIndex: number = yield call(findStepIndexByName, steps, currentStepName);

  const newIndex = currentStepIndex + offset;

  yield put(commitWorkflowStepById(workflowId, currentStepName, values));

  if (newIndex >= 0 && newIndex <= steps.length - 1) {
    const newStep = steps[newIndex].props.name;
    yield put(moveWorkflowToStepById(workflowId, newStep, { replace, state }));
  }
}

function* moveBackward(argsGetter: () => WorkflowComposedProps, { values, replace, state }: MoveWorkflowBackwardById | MoveWorkflowBackwardByName) {
  yield moveStep(argsGetter, -1, values, { replace, state });
}

function* moveForward(argsGetter: () => WorkflowComposedProps, { values, replace, state }: MoveWorkflowForwardById | MoveWorkflowForwardByName) {
  yield moveStep(argsGetter, 1, values, { replace, state });
}

function* listenToRouteChange(argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { history } = props;

  const historyChannel = eventChannel((emit) => {
    const unlisten = history.listen((...args) => {
      // Needs a timer to let the workflow unmount during navigation
      emit(args);
    });
    return unlisten;
  });

  try {
    while (true) {
      const lastLocation = { ...history.location };

      const results: {
        exitGuardsResult: Promise<void>[];
        navigation: [HistoryLocation];
      } = yield all({
        exitGuardsResult: call(handleExitGuards, lastLocation, argsGetter),
        navigation: take(historyChannel),
      });

      const { navigation: [newLocation] } = results;

      const isEntryBlocked: boolean = yield call(handleEntryGuards, lastLocation, newLocation, argsGetter);

      if (!isEntryBlocked) {
        const activeStep: ReactElement<StepProps> = yield call(getActiveStepFromLocation, props);
        if (!activeStep) {
          yield spawn(function* redirect() {
          // Give some for component to be unmounted in case the route leave the workflow
            yield delay(0);
            yield call(loadActiveStep, argsGetter);
          });
        } else {
          yield call(loadActiveStep, argsGetter);
        }
      }
    }
  } finally {
    historyChannel.close();
  }
}

function* handleExitGuards(lastLocation: HistoryLocation, argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { history } = props;

  const activeStep: ReactElement<StepProps> = yield call(getActiveStepFromLocation, props);

  if (activeStep?.props?.exitGuards) {
    const exitGuardChannel = eventChannel<HistoryLocation>((emit) => {
      const unblock = history.block((newLocation) => {
        emit(newLocation);
        return false;
      });

      return () => {
        unblock();
      };
    });

    while (true) {
      const newLocation: HistoryLocation = yield take(exitGuardChannel);

      const isExitGuarded: boolean = yield call(applyExitGuards, activeStep, lastLocation, newLocation, argsGetter);

      if (!isExitGuarded) {
        yield exitGuardChannel.close();
        // Required for react-router v5
        yield call([history, history.replace], newLocation.pathname);
        return;
      }
    }
  }
}

function* applyExitGuards(activeStep: ReactElement<StepProps>, lastLocation: HistoryLocation, newLocation: HistoryLocation, argsGetter: () => WorkflowComposedProps) {
  for (let i = 0, l = activeStep?.props?.exitGuards.length; i < l; i++) {
    const shouldContinue: boolean = yield call(handleExitGuard, activeStep, i, lastLocation, newLocation, argsGetter);

    if (!shouldContinue) {
      return true;
    }
  }

  return false;
}

function* handleExitGuard(activeStep: ReactElement<StepProps>, i: number, lastLocation: HistoryLocation, newLocation: HistoryLocation, argsGetter: () => WorkflowComposedProps) {
  const { history, workflowId } = argsGetter();

  const guard = activeStep?.props?.exitGuards[i];
  const guardOptions: ExitGuardOptions = {
    from: { ...lastLocation },
    to: { ...newLocation },
    redirect: (url, options: { state?: any }) => ({ type: GUARD_ACTION_TYPES.REDIRECT, url, options }),
    block: () => ({ type: GUARD_ACTION_TYPES.BLOCK }),
    confirm: () => ({ type: GUARD_ACTION_TYPES.CONFIRM }),
  };

  const guardAction: RedirectGuardAction | BlockGuardAction | ConfirmGuardAction = yield call(guard, guardOptions);

  switch (guardAction?.type) {
    case GUARD_ACTION_TYPES.CONFIRM: {
      const modalName = `workflow${workflowId}ConfirmModal`;
      try {
        yield put(openModalByName(modalName));
        const {
          confirmation,
        } = yield race({
          cancellation: take(`${cancelWorkflowStepEntryById.type}/${workflowId}`),
          confirmation: take(`${confirmWorkflowStepEntryById.type}/${workflowId}`),
        });

        if (confirmation) {
          return true;
        }
      } finally {
        yield put(closeModalByName(modalName));
      }
      return false;
    }
    case GUARD_ACTION_TYPES.BLOCK: {
      return false;
    }
    case GUARD_ACTION_TYPES.REDIRECT:
      yield call([history, history.replace], guardAction.url, guardAction.state);
      return false;
    default:
      return true;
  }
}

function* handleEntryGuards(lastLocation: HistoryLocation, newLocation: HistoryLocation, argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const nextStep: ReactElement<StepProps> = yield call(getStepByPathname, props, newLocation.pathname, props.basename);

  if (nextStep?.props?.entryGuards) {
    for (let i = 0, l = nextStep?.props?.entryGuards.length; i < l; i++) {
      const shouldContinue: boolean = yield call(handleEntryGuard, nextStep, i, lastLocation, newLocation, argsGetter);
      if (!shouldContinue) return true;
    }
  }

  return false;
}

function* handleEntryGuard(nextStep: ReactElement<StepProps>, i: number, lastLocation: HistoryLocation, newLocation: HistoryLocation, argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { history } = props;

  const guard = nextStep?.props?.entryGuards[i];
  const guardOptions: EntryGuardOptions = {
    from: { ...lastLocation },
    to: { ...newLocation },
    redirect: (url, options: { state?: any }) => ({ type: GUARD_ACTION_TYPES.REDIRECT, url, state: options?.state }),
    backward: (options: { state?: any }) => ({ type: GUARD_ACTION_TYPES.BACKWARD, state: options?.state }),
    forward: (options: { state?: any }) => ({ type: GUARD_ACTION_TYPES.FORWARD, state: options?.state }),
    block: () => ({ type: GUARD_ACTION_TYPES.BLOCK }),
  };

  const guardAction: RedirectGuardAction | BlockGuardAction | BackwardGuardAction | ForwardGuardAction = yield call(guard, guardOptions);

  switch (guardAction?.type) {
    case GUARD_ACTION_TYPES.BLOCK: {
      yield call([history, history.replace], lastLocation.pathname);
      return false;
    }
    case GUARD_ACTION_TYPES.REDIRECT:
      // Must be non-blocking to give time to the handler to listen to the new navigation
      yield spawn(function* redirect() {
        yield delay(0);
        yield call([history, history.replace], guardAction.url);
      });
      return false;
    case GUARD_ACTION_TYPES.BACKWARD: {
      // Must be non-blocking to give time to the handler to listen to the new navigation
      yield spawn(function* redirect() {
        yield delay(0);
        yield call(moveToStepByOffset, -1, nextStep, guardAction.state, argsGetter);
      });
      return false;
    }
    case GUARD_ACTION_TYPES.FORWARD: {
      // Must be non-blocking to give time to the handler to listen to the new navigation
      yield spawn(function* redirect() {
        yield delay(0);
        yield call(moveToStepByOffset, 1, nextStep, guardAction.state, argsGetter);
      });
      return false;
    }
    default:
      return true;
  }
}

function* moveToStepByOffset(offset: number, currentStep: ReactElement<StepProps>, state: any, argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { history, basename } = props;
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const currentStepIndex: number = yield call(findStepIndexByName, steps, currentStep.props.name);
  const targetStep = steps[currentStepIndex + offset];

  if (targetStep) {
    const locale: string = yield select(({ language }: { language: LanguageState }) => language?.locale);
    const languageCode = getLanguageCode(locale);
    const path = (typeof targetStep.props.path === 'string' || Array.isArray(targetStep.props.path)) ? targetStep.props.path : targetStep.props.path[languageCode];
    const resolvedPath = Array.isArray(path) ? path[0] : path;
    const stepPathname = normalizePath(resolvedPath, basename);
    yield call([history, history.replace], stepPathname, state);
  }
}

function* moveToFirstStep(argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { workflowId, initialStepName } = props;
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const firstStep = steps[0];
  const resolvedInitialStepName = initialStepName || firstStep?.props.name || null;
  const step: ReactElement<StepProps> = yield call(findStepByName, steps, resolvedInitialStepName);
  const nextStepName = step ? step.props.name : null;

  yield put(moveWorkflowToStepById(workflowId, nextStepName));
}

function* loadActiveStep(argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { workflowId } = props;

  const activeStep: ReactElement<StepProps> = yield call(getActiveStepFromLocation, props);

  if (activeStep) {
    yield put(workflowMovedToStepById(workflowId, activeStep.props.name));
  } else {
    yield call(moveToFirstStep, argsGetter);
  }
}

function* moveToStep(argsGetter: () => WorkflowComposedProps, { stepName, replace, state }: MoveWorkflowToStepById | MoveWorkflowToStepByName): any {
  const props = argsGetter();
  const { workflowId, history, basename } = props;
  const steps: ReactElement<StepProps>[] = yield call(getSteps, props);
  const step: ReactElement<StepProps> = yield call(findStepByName, steps, stepName);

  if (step?.props?.path) {
    const locale: string = yield select(({ language }: { language: LanguageState }) => language?.locale);
    const languageCode = getLanguageCode(locale);
    const path = (typeof step.props.path === 'object' && !Array.isArray(step.props.path)) ? step.props.path[languageCode] : step.props.path;
    const resolvedPath = Array.isArray(path) ? path[0] : path;
    const stepPathname = normalizePath(resolvedPath, basename);

    const alreadyOnRoute = Router.matchPath(history.location.pathname, {
      path: stepPathname,
    });

    if (!alreadyOnRoute) {
      if (replace) {
        yield call([history, history.replace], stepPathname, state);
      } else {
        yield call([history, history.push], stepPathname, { state });
      }
    }
  } else if (step) {
    yield put(workflowMovedToStepById(workflowId, step.props.name));
  }
}

export default function* workflowSaga(argsGetter: () => WorkflowComposedProps) {
  const props = argsGetter();
  const { workflowId, name, history } = props;

  yield takeLatest([
    `${moveWorkflowBackwardById.type}/${workflowId}`,
    `${moveWorkflowBackwardByName.type}/${name}`,
  ], moveBackward, argsGetter);

  yield takeLatest([
    `${moveWorkflowForwardById.type}/${workflowId}`,
    `${moveWorkflowForwardByName.type}/${name}`,
  ], moveForward, argsGetter);

  yield takeLatest([
    `${moveWorkflowToStepById.type}/${workflowId}`,
    `${moveWorkflowToStepByName.type}/${name}`,
  ], moveToStep, argsGetter);

  yield fork(listenToRouteChange, argsGetter);

  // Move out of the component rendering, which cause a warning in react-router 6+
  yield delay(0);
  const isEntryBlocked: boolean = yield call(handleEntryGuards, null, history.location, argsGetter);
  if (!isEntryBlocked) {
    yield call(loadActiveStep, argsGetter);
  } else {
    yield call(moveToFirstStep, argsGetter);
  }
}
